import React, { useEffect } from 'react';
import { Text } from '@cotera/client/app/components/ui';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import {
  useDatasetOptions,
  useDatasetShape,
} from '@cotera/client/app/v1/trigger/hooks';
import { useWorkflowDatasetTrigger } from '../../../../hooks/use-workflow-dataset-trigger';
import type { ToolInputControlProps } from './index';

/**
 * Picks the dataset a tool acts on, by name.
 *
 * The field is a uuid, and the generic editor renders a uuid as a text box —
 * which asks the person building the workflow to know an id they have no way to
 * look up from here. The options come from the same live resource stream the
 * trigger wizard uses, so a dataset created in another tab appears without
 * reopening the drawer.
 *
 * The only thing said about the dataset here is the one fact that makes every
 * mode impossible: rows with no durable id cannot be addressed at all. How an
 * *updated* row is found is a question about updating, and belongs with it.
 *
 * An unset field answers itself when the workflow is fired by a dataset. That is
 * the commonest workflow there is — read or enrich the row that changed — and
 * the alternative was asking the builder to pick, out of every dataset in the
 * org, the one it had already named as the trigger.
 */
export const DatasetSelectControl: React.FC<ToolInputControlProps> = ({
  field,
  params,
  setValues,
}) => {
  const datasets = useDatasetOptions();
  const raw = params[field.name];
  const value = typeof raw === 'string' && raw !== '' ? raw : null;
  const known = datasets.some((dataset) => dataset.id === value);
  const shape = useDatasetShape(value);
  const triggerDatasetId = useWorkflowDatasetTrigger()?.datasetId ?? null;

  /**
   * Seeds the trigger's dataset into an unset field.
   *
   * Only when unset, so it is a default and not a correction: a node pointed at
   * a second dataset deliberately keeps pointing there. In an effect rather than
   * at render because the trigger arrives with the graph, which the drawer can
   * open before.
   *
   * It commits the same way a hand-picked dataset would — this editor autosaves
   * — which is the point: a seed the user has to confirm is not a saving of
   * anything. What stops it running away with a node is that it only ever fires
   * on a node that names no dataset at all, and such a node is missing a
   * required input and could not have run.
   */
  useEffect(() => {
    if (value !== null || triggerDatasetId === null) {
      return;
    }
    setValues({ [field.name]: triggerDatasetId });
    // `setValues` is rebuilt every render by the row that owns this field;
    // depending on it would re-run this on every one. The guard above is what
    // makes it run once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, triggerDatasetId, field.name]);

  return (
    <div className="flex flex-col gap-1.5">
      <Select
        value={value ?? undefined}
        onValueChange={(next) => setValues({ [field.name]: next })}
      >
        {/* `size` rather than a height class: the trigger's own
            `data-[size]` rule outranks a plain one, so a class here reads as a
            change and renders as nothing. */}
        <SelectTrigger size="sm" className="w-full">
          <SelectValue placeholder="Choose a dataset" />
        </SelectTrigger>
        <SelectContent>
          {/* An id that is not in the list — archived, or from another org —
              still renders as itself. Dropping it would silently blank a
              configured node and make the change look like the user's. */}
          {value !== null && !known ? (
            <SelectItem value={value}>Unavailable dataset ({value})</SelectItem>
          ) : null}
          {datasets.map((dataset) => (
            <SelectItem key={dataset.id} value={dataset.id}>
              {dataset.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {shape !== null && shape.rowIdColumn === null ? (
        // Not offered a fix here on purpose: declaring row keys is the obvious
        // guess and the wrong answer, since an edit targets the artifact's
        // internal id column and no declaration creates one.
        <Text.Caption className="text-destructive">
          This dataset&apos;s rows cannot be addressed individually, so it
          cannot be written to. It predates durable row ids — re-uploading or
          recreating it will fix that.
        </Text.Caption>
      ) : null}
    </div>
  );
};
